import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { companyTypeOptions as defaultCompanyTypeOptions } from '../../lib/assets';
import {
  Building2,
  Plus,
  Pencil,
  Trash2,
  Shield,
  Search,
  BriefcaseBusiness,
  Tags,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyCompanyForm = {
  org_id: '',
  company_type: 'spv',
  name: '',
  contact_person: '',
  contact_email: '',
  contact_phone: '',
  description: '',
  status: 'active',
};

function companyTypeLabel(type) {
  return defaultCompanyTypeOptions.find((option) => option.value === type)?.label || type;
}

export default function CompaniesPage() {
  const { org, hasRole } = useAuth();
  const isPlatformAdmin = hasRole(['superadmin', 'platform_admin']);
  const canManage = hasRole(['superadmin', 'platform_admin', 'org_admin']);
  const [organizations, setOrganizations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyTypes, setCompanyTypes] = useState(defaultCompanyTypeOptions);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState(emptyCompanyForm);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [companyTypeForm, setCompanyTypeForm] = useState({
    label: '',
    description: '',
    status: 'active',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedOrg !== 'all') params.append('org_id', selectedOrg);
      if (selectedType !== 'all') params.append('company_type', selectedType);

      const [organizationsRes, companiesRes, companyTypesRes] = await Promise.all([
        isPlatformAdmin ? axios.get(`${API}/organizations`) : Promise.resolve({ data: org ? [org] : [] }),
        axios.get(`${API}/companies?${params.toString()}`),
        axios.get(`${API}/company-types`),
      ]);
      setOrganizations(organizationsRes.data);
      setCompanies(companiesRes.data);
      setCompanyTypes(companyTypesRes.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin, org, selectedOrg, selectedType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const availableOrganizations = organizations.length > 0 ? organizations : org ? [org] : [];
  const availableCompanyTypes = companyTypes.length > 0 ? companyTypes : defaultCompanyTypeOptions;

  const visibleCompanies = useMemo(() => {
    const query = searchTerm.toLowerCase();
    return companies.filter((company) => {
      if (!query) return true;
      return (
        company.name?.toLowerCase().includes(query) ||
        company.contact_person?.toLowerCase().includes(query) ||
        company.contact_email?.toLowerCase().includes(query) ||
        company.org_name?.toLowerCase().includes(query)
      );
    });
  }, [companies, searchTerm]);

  const visibleCompanyTypes = useMemo(() => {
    const query = searchTerm.toLowerCase();
    return availableCompanyTypes.filter((type) => {
      if (!query) return true;
      return (
        type.label?.toLowerCase().includes(query) ||
        type.description?.toLowerCase().includes(query)
      );
    });
  }, [availableCompanyTypes, searchTerm]);

  const getCompanyTypeLabel = (type) =>
    availableCompanyTypes.find((option) => option.value === type || option.code === type)?.label ||
    companyTypeLabel(type);

  const openCreate = () => {
    setEditingCompany(null);
    setCompanyForm({
      ...emptyCompanyForm,
      org_id: selectedOrg !== 'all' ? selectedOrg : org?.id || '',
    });
    setDialogOpen(true);
  };

  const openEdit = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      org_id: company.org_id || '',
      company_type: company.company_type || 'spv',
      name: company.name || '',
      contact_person: company.contact_person || '',
      contact_email: company.contact_email || '',
      contact_phone: company.contact_phone || '',
      description: company.description || '',
      status: company.status || 'active',
    });
    setDialogOpen(true);
  };

  const saveCompany = async () => {
    if (!companyForm.org_id || !companyForm.company_type || !companyForm.name.trim()) {
      toast.error('Organization, company type, and company name are required');
      return;
    }
    try {
      if (editingCompany) {
        await axios.patch(`${API}/companies/${editingCompany.id}`, companyForm);
      } else {
        await axios.post(`${API}/companies`, companyForm);
      }
      setDialogOpen(false);
      setCompanyForm(emptyCompanyForm);
      fetchData();
    } catch (error) {
      console.error('Failed to save company:', error);
      toast.error(error.response?.data?.detail || 'Failed to save company');
    }
  };

  const deleteCompany = async (company) => {
    if (!window.confirm(`Delete company "${company.name}"?`)) return;
    try {
      await axios.delete(`${API}/companies/${company.id}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete company:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    }
  };

  const openCreateType = () => {
    setEditingType(null);
    setCompanyTypeForm({
      label: '',
      description: '',
      status: 'active',
    });
    setTypeDialogOpen(true);
  };

  const openEditType = (type) => {
    setEditingType(type);
    setCompanyTypeForm({
      label: type.label || '',
      description: type.description || '',
      status: type.status || 'active',
    });
    setTypeDialogOpen(true);
  };

  const saveCompanyType = async () => {
    if (!companyTypeForm.label.trim()) {
      toast.error('Company type label is required');
      return;
    }
    try {
      if (editingType) {
        await axios.patch(`${API}/company-types/${editingType.id}`, companyTypeForm);
      } else {
        await axios.post(`${API}/company-types`, companyTypeForm);
      }
      setTypeDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save company type:', error);
      toast.error(error.response?.data?.detail || 'Failed to save company type');
    }
  };

  const deleteCompanyType = async (type) => {
    if (!window.confirm(`Delete company type "${type.label}"?`)) return;
    try {
      await axios.delete(`${API}/company-types/${type.id}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete company type:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete company type');
    }
  };

  if (!canManage) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-500">You do not have permission to manage companies.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Companies</h1>
            <p className="text-slate-500">
              Manage company records by type and assign them to assets from dropdowns.
            </p>
          </div>
          <div className="flex gap-2">
            {isPlatformAdmin && (
              <Button variant="outline" onClick={openCreateType}>
                <Tags className="w-4 h-4 mr-2" />
                Add Company Type
              </Button>
            )}
            <Button onClick={openCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Add Company
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">Companies</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">{companies.length}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">Types in Use</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {new Set(companies.map((company) => company.company_type)).size}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">Organizations Covered</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {new Set(companies.map((company) => company.org_id)).size}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search company, contact, or organization..."
                  className="pl-10"
                />
              </div>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="w-full lg:w-[220px]">
                  <SelectValue placeholder="Filter company type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All company types</SelectItem>
                  {availableCompanyTypes.map((option) => (
                    <SelectItem key={option.value || option.code} value={option.value || option.code}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                <SelectTrigger className="w-full lg:w-[260px]">
                  <SelectValue placeholder="Filter organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All organizations</SelectItem>
                  {availableOrganizations.map((organization) => (
                    <SelectItem key={organization.id} value={organization.id}>
                      {organization.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="companies" className="space-y-4">
          <TabsList>
            <TabsTrigger value="companies">Companies</TabsTrigger>
            {isPlatformAdmin && <TabsTrigger value="company-types">Company Types</TabsTrigger>}
          </TabsList>

          <TabsContent value="companies">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Company Master</CardTitle>
                <CardDescription>
                  Company records are org-owned and can be assigned to assets from dropdowns.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {loading ? (
                  <p className="text-slate-500">Loading companies...</p>
                ) : visibleCompanies.length === 0 ? (
                  <p className="text-slate-500">No companies found.</p>
                ) : (
                  visibleCompanies.map((company) => (
                    <Card key={company.id} className="border-slate-200">
                      <CardContent className="p-5 space-y-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-slate-900">{company.name}</h3>
                            <p className="text-sm text-slate-500 mt-1">{company.description || company.org_name}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => openEdit(company)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => deleteCompany(company)}>
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
                            {getCompanyTypeLabel(company.company_type)}
                          </Badge>
                          <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200">
                            {company.org_name || 'No org'}
                          </Badge>
                          <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">
                            Added by {company.added_by_user_name || '-'}
                          </Badge>
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            {company.status || 'active'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Contact Person</p>
                            <p className="font-medium text-slate-900">{company.contact_person || 'Not set'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Email</p>
                            <p className="font-medium text-slate-900">{company.contact_email || 'Not set'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Phone</p>
                            <p className="font-medium text-slate-900">{company.contact_phone || 'Not set'}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {isPlatformAdmin && (
            <TabsContent value="company-types">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Company Types</CardTitle>
                  <CardDescription>
                    Managed only by Admin and Super Admin. System types keep asset mappings stable.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loading ? (
                    <p className="text-slate-500">Loading company types...</p>
                  ) : visibleCompanyTypes.length === 0 ? (
                    <p className="text-slate-500">No company types found.</p>
                  ) : (
                    visibleCompanyTypes.map((type) => (
                      <Card key={type.id || type.code} className="border-slate-200">
                        <CardContent className="p-5 space-y-4">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h3 className="font-semibold text-slate-900">{type.label}</h3>
                              <p className="text-sm text-slate-500 mt-1">{type.description || 'No description'}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="outline" size="sm" onClick={() => openEditType(type)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Edit
                              </Button>
                              {!type.is_system && (
                                <Button variant="destructive" size="sm" onClick={() => deleteCompanyType(type)}>
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                              {type.status || 'active'}
                            </Badge>
                            {type.is_system && (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                System Type
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Edit Company' : 'Add Company'}</DialogTitle>
            <DialogDescription>
              Platform admins can assign any organization. Org admins are limited to their own organization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Organization</Label>
              <Select
                value={companyForm.org_id || 'unselected'}
                disabled={!isPlatformAdmin || Boolean(editingCompany)}
                onValueChange={(value) =>
                  setCompanyForm((state) => ({ ...state, org_id: value === 'unselected' ? '' : value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unselected">Select organization</SelectItem>
                  {availableOrganizations.map((organization) => (
                    <SelectItem key={organization.id} value={organization.id}>
                      {organization.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Company Type</Label>
              <Select
                value={companyForm.company_type}
                onValueChange={(value) => setCompanyForm((state) => ({ ...state, company_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableCompanyTypes.map((option) => (
                    <SelectItem key={option.value || option.code} value={option.value || option.code}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={companyForm.name}
                onChange={(e) => setCompanyForm((state) => ({ ...state, name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Contact Person</Label>
                <Input
                  value={companyForm.contact_person}
                  onChange={(e) => setCompanyForm((state) => ({ ...state, contact_person: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={companyForm.contact_email}
                  onChange={(e) => setCompanyForm((state) => ({ ...state, contact_email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={companyForm.contact_phone}
                  onChange={(e) => setCompanyForm((state) => ({ ...state, contact_phone: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={companyForm.description}
                onChange={(e) => setCompanyForm((state) => ({ ...state, description: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={companyForm.status}
                onValueChange={(value) => setCompanyForm((state) => ({ ...state, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={saveCompany}>
                <BriefcaseBusiness className="w-4 h-4 mr-2" />
                {editingCompany ? 'Save Changes' : 'Create Company'}
              </Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Company Type' : 'Add Company Type'}</DialogTitle>
            <DialogDescription>
              Company types are managed only by Admin and Super Admin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Label</Label>
              <Input
                value={companyTypeForm.label}
                onChange={(e) => setCompanyTypeForm((state) => ({ ...state, label: e.target.value }))}
              />
              <p className="text-xs text-slate-500">
                Code is auto-generated from this label in lowercase, with special characters replaced by spaces.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={companyTypeForm.description}
                onChange={(e) => setCompanyTypeForm((state) => ({ ...state, description: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={companyTypeForm.status}
                onValueChange={(value) => setCompanyTypeForm((state) => ({ ...state, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={saveCompanyType}>
                <Tags className="w-4 h-4 mr-2" />
                {editingType ? 'Save Changes' : 'Create Company Type'}
              </Button>
              <Button variant="outline" onClick={() => setTypeDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
